import React, { useEffect, useState } from "react";
import { Form, Select, Table, Button, message, Row, Col, Typography, Empty } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { BsPlus } from "react-icons/bs";
import { AppPageHeader } from "components/AppPageHeader";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { getDarkStores, getCities } from "store/admin-slice/adminServicesSlice";
import { getStoreConfig, removeStoreConfig } from "store/ppd/ppdSlice";
import { useNavigate } from "react-router-dom";
import { routes } from "common/routes";

const MESSAGE_KEY = "store-config";
const { Text } = Typography;

const StoreConfigsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const { storeConfig, status, error } = useAppSelector((state) => state.ppd);
  const { darkStores, cities } = useAppSelector((state) => state.adminServices);

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
    return () => {
      dispatch(removeStoreConfig());
    };
  }, []);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading...", key: MESSAGE_KEY });
    } else if (status === "success" && hasAttemptedFetch) {
      message.success({ content: "Success!", key: MESSAGE_KEY, duration: 2 });
    } else if (status === "error" && hasAttemptedFetch) {
      message.error({ content: error, key: MESSAGE_KEY, duration: 3 });
    }
    return () => {
      message.destroy();
    };
  }, [status, error, hasAttemptedFetch]);

  useEffect(() => {
    if (storeId != undefined) {
      setHasAttemptedFetch(true);
      dispatch(getStoreConfig(storeId));
    }
  }, [storeId]);

  const handleEdit = () => {
    if (storeId) {
      navigate(`/${routes.PPD.EDIT_STORE_CONFIG.replace(":storeId", storeId)}`);
    }
  };

  const redirectToNewConfigPage = () => {
    navigate(`/${routes.PPD.NEW_STORE_CONFIG}`);
  };

  const columns = [
    {
      title: "Store ID",
      dataIndex: "storeId",
      key: "storeId",
      width: 100,
    },
    {
      title: "Pickers",
      dataIndex: "pickers",
      key: "pickers",
      width: 80,
    },
    {
      title: "Packers",
      dataIndex: "packers",
      key: "packers",
      width: 80,
    },
    {
      title: "Dispatchers",
      dataIndex: "dispatchers",
      key: "dispatchers",
      width: 100,
    },
    {
      title: "EV Count",
      dataIndex: "evCount",
      key: "evCount",
      width: 90,
    },
    {
      title: "Auto Count",
      dataIndex: "autoCount",
      key: "autoCount",
      width: 100,
    },
    {
      title: "Bike Count",
      dataIndex: "bikeCount",
      key: "bikeCount",
      width: 100,
    },
    {
      title: "EV L3 Count",
      dataIndex: "evL3Count",
      key: "evL3Count",
      width: 110,
    },
    {
      title: "Helper Count",
      dataIndex: "helperCount",
      key: "helperCount",
      width: 110,
    },
    {
      title: "Dock Count",
      dataIndex: "dockCount",
      key: "dockCount",
      width: 100,
    },
    {
      title: "Inter Society Avg Distance (km)",
      dataIndex: "interSocietyAvgDistanceKm",
      key: "interSocietyAvgDistanceKm",
      width: 200,
    },
    {
      title: "Inter Society Avg Travel Time (mins)",
      dataIndex: "interSocietyAvgTravelTimeMins",
      key: "interSocietyAvgTravelTimeMins",
      width: 230,
    },
    {
      title: "Avg Delivery Time Per Order (mins)",
      dataIndex: "avgDeliveryTimePerOrderMins",
      key: "avgDeliveryTimePerOrderMins",
      width: 220,
    },
    {
      title: "Avg Entry Time Per Society (mins)",
      dataIndex: "avgEntryTimePerSocietyMins",
      key: "avgEntryTimePerSocietyMins",
      width: 220,
    },
    {
      title: "Avg Pick Time Per Line Item (mins)",
      dataIndex: "avgPickTimePerLineItemMins",
      key: "avgPickTimePerLineItemMins",
      width: 230,
    },
    {
      title: "Avg Pack Time Per Order (mins)",
      dataIndex: "avgPackTimePerOrderMins",
      key: "avgPackTimePerOrderMins",
      width: 210,
    },
    {
      title: "Avg Dispatch Time Per Order (mins)",
      dataIndex: "avgDispatchTimePerOrderMins",
      key: "avgDispatchTimePerOrderMins",
      width: 230,
    },
    {
      title: "Pick Pack Start Time",
      dataIndex: "pickPackStartTime",
      key: "pickPackStartTime",
      width: 160,
    },
    {
      title: "Dispatch Start Time",
      dataIndex: "dispatchStartTime",
      key: "dispatchStartTime",
      width: 160,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 100,
      render: () => (
        <Button
          icon={<EditOutlined />}
          onClick={handleEdit}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  const getTableData = () => {
    if (!storeConfig) return [];
    return [storeConfig];
  };

  const renderContent = () => {
    // Show loading state
    if (status === "loading" && hasAttemptedFetch) {
      return null; // Table will show loading
    }

    // Show "No Configs Found" when:
    // 1. API call was made (hasAttemptedFetch is true)
    // 2. Status is success or error
    // 3. No storeConfig data exists
    if (hasAttemptedFetch && !storeConfig && (status === "success" || status === "error")) {
      return (
        <Row justify="center" align="middle" style={{ minHeight: "400px" }}>
          <Col>
            <Empty
              description={
                <Text type="secondary" style={{ fontSize: 16 }}>
                  No Configs Found for this Store
                </Text>
              }
            />
          </Col>
        </Row>
      );
    }

    // Show table when data exists
    if (storeConfig && storeId) {
      return (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={getTableData()}
              pagination={false}
              bordered
              size="small"
              loading={status === "loading"}
              scroll={{ x: 2500, y: "65vh" }}
              rowKey="storeId"
            />
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Store Configs" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <Button
            onClick={redirectToNewConfigPage}
            type="primary"
            className="action-strip__item"
            icon={<BsPlus />}
          >
            New Config
          </Button>
        </AppActionStrip>
      </Row>
      <Form layout="vertical" className="mb-8">
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="City"
              name="cityId"
              className="mb-4"
            >
              <Select
                showSearch
                placeholder="Select city"
                className="w-full"
                filterOption={(input, option) => {
                  return option?.children?.toString()
                    .toLowerCase()
                    .includes(input?.toLowerCase() ?? "") ?? false;
                }}
                onChange={(e) => {
                  setCityId(e);
                  setStoreId(undefined);
                  setHasAttemptedFetch(false);
                  form.setFieldsValue({ storeId: undefined });
                  dispatch(removeStoreConfig());
                }}
              >
                {(cities ?? []).map((city) => (
                  <Select.Option value={city.id} key={city.id}>
                    {city.name} || {city.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {cityId && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label="Store ID"
                name="storeId"
                className="mb-4"
              >
                <Select
                  showSearch
                  placeholder="Select store"
                  className="w-full"
                  value={storeId}
                  filterOption={(input, option) => {
                    return option?.children?.toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() ?? "") ?? false;
                  }}
                  onChange={(e) => {
                    setStoreId(e);
                    dispatch(removeStoreConfig());
                  }}
                >
                  {(darkStores ?? [])
                    .filter(store => store?.cityId?.toString() == cityId)
                    .map((storeInfo) => (
                      <Select.Option value={storeInfo.id} key={storeInfo.id}>
                        {storeInfo.name} || {storeInfo.id}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {renderContent()}
    </>
  );
};

export default StoreConfigsPage;
